import { useState, useEffect, useCallback } from 'react';

interface NetworkRequest {
  id: string;
  method: string;
  url: string;
  status: number | null;
  statusText: string;
  duration: number | null;
  timestamp: Date;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: string;
}

// Global request store
const networkRequests: NetworkRequest[] = [];
const listeners: Set<() => void> = new Set();

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

// Intercept fetch
const originalFetch = window.fetch;
window.fetch = async function(...args) {
  const id = Math.random().toString(36).substring(7);
  const startTime = performance.now();
  const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request).url;
  const method = args[1]?.method || 'GET';
  
  const request: NetworkRequest = {
    id,
    method: method.toUpperCase(),
    url,
    status: null,
    statusText: 'Pending...',
    duration: null,
    timestamp: new Date(),
    requestBody: args[1]?.body,
  };
  
  networkRequests.unshift(request);
  if (networkRequests.length > 100) networkRequests.pop();
  notifyListeners();
  
  try {
    const response = await originalFetch.apply(this, args);
    const duration = performance.now() - startTime;
    
    // Clone response to read body
    const clone = response.clone();
    let responseBody;
    try {
      responseBody = await clone.json();
    } catch {
      try {
        responseBody = await clone.text();
      } catch {
        responseBody = '[Unable to parse]';
      }
    }
    
    Object.assign(request, {
      status: response.status,
      statusText: response.statusText,
      duration: Math.round(duration),
      responseBody,
    });
    notifyListeners();
    
    return response;
  } catch (error) {
    const duration = performance.now() - startTime;
    Object.assign(request, {
      status: 0,
      statusText: 'Network Error',
      duration: Math.round(duration),
      error: String(error),
    });
    notifyListeners();
    throw error;
  }
};

export const NetworkMonitor = () => {
  const [requests, setRequests] = useState<NetworkRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<NetworkRequest | null>(null);
  const [filter, setFilter] = useState<'all' | 'error' | 'slow'>('all');
  
  const updateRequests = useCallback(() => {
    setRequests([...networkRequests]);
  }, []);
  
  useEffect(() => {
    listeners.add(updateRequests);
    updateRequests();
    return () => { listeners.delete(updateRequests); };
  }, [updateRequests]);
  
  const filteredRequests = requests.filter(r => {
    if (filter === 'error') return r.status === 0 || (r.status && r.status >= 400);
    if (filter === 'slow') return r.duration && r.duration > 1000;
    return true;
  });
  
  const stats = {
    total: requests.length,
    errors: requests.filter(r => r.status === 0 || (r.status && r.status >= 400)).length,
    avgLatency: requests.length > 0 
      ? Math.round(requests.filter(r => r.duration).reduce((a, b) => a + (b.duration || 0), 0) / requests.filter(r => r.duration).length)
      : 0,
  };
  
  const getStatusColor = (status: number | null) => {
    if (!status) return 'text-gray-400';
    if (status < 300) return 'text-green-400';
    if (status < 400) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  const clearRequests = () => {
    networkRequests.length = 0;
    setRequests([]);
    setSelectedRequest(null);
  };

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 rounded-lg p-3 border border-blue-500/30">
        <p className="text-sm">
          <span className="text-blue-400 font-medium">Network Summary:</span>{' '}
          <span className="text-gray-200">
            {stats.total} requests tracked, {stats.errors > 0 ? (
              <span className="text-red-400">{stats.errors} failed</span>
            ) : (
              <span className="text-green-400">all successful</span>
            )}, avg latency {stats.avgLatency}ms
          </span>
        </p>
      </div>
      
      {/* Controls */}
      <div className="flex items-center gap-2">
        <select 
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        >
          <option value="all">All Requests</option>
          <option value="error">Errors Only</option>
          <option value="slow">Slow (&gt;1s)</option>
        </select>
        <button
          onClick={clearRequests}
          className="bg-red-600/50 hover:bg-red-600 px-2 py-1 rounded text-xs"
        >
          🗑️ Clear
        </button>
      </div>
      
      {/* Request List */}
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {filteredRequests.length === 0 ? (
          <p className="text-gray-500 text-xs text-center py-4">No requests captured yet</p>
        ) : (
          filteredRequests.map((req) => (
            <div
              key={req.id}
              onClick={() => setSelectedRequest(req)}
              className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs ${
                selectedRequest?.id === req.id ? 'bg-purple-900/50 border border-purple-500' : 'bg-gray-800 hover:bg-gray-750'
              }`}
            >
              <span className={`font-mono font-bold w-12 ${
                req.method === 'GET' ? 'text-green-400' :
                req.method === 'POST' ? 'text-blue-400' :
                req.method === 'PUT' ? 'text-yellow-400' :
                req.method === 'DELETE' ? 'text-red-400' : 'text-gray-400'
              }`}>
                {req.method}
              </span>
              <span className={`w-10 text-right ${getStatusColor(req.status)}`}>
                {req.status || '...'}
              </span>
              <span className="text-gray-400 flex-1 truncate">{req.url.replace(/^https?:\/\/[^/]+/, '')}</span>
              <span className="text-gray-500 w-16 text-right">
                {req.duration ? `${req.duration}ms` : '...'}
              </span>
            </div>
          ))
        )}
      </div>
      
      {/* Selected Request Details */}
      {selectedRequest && (
        <div className="bg-gray-800 rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-purple-400">Request Details</h4>
            <button onClick={() => setSelectedRequest(null)} className="text-gray-500 hover:text-white text-xs">✕</button>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-gray-500">URL:</span> <span className="text-gray-300 break-all">{selectedRequest.url}</span></div>
            <div><span className="text-gray-500">Method:</span> <span className="text-gray-300">{selectedRequest.method}</span></div>
            <div><span className="text-gray-500">Status:</span> <span className={getStatusColor(selectedRequest.status)}>{selectedRequest.status} {selectedRequest.statusText}</span></div>
            <div><span className="text-gray-500">Duration:</span> <span className="text-gray-300">{selectedRequest.duration}ms</span></div>
          </div>
          
          {selectedRequest.responseBody !== undefined && (
            <div>
              <span className="text-gray-500 text-xs">Response Body:</span>
              <pre className="bg-gray-900 rounded p-2 mt-1 text-xs overflow-auto max-h-32">
                {typeof selectedRequest.responseBody === 'string' 
                  ? selectedRequest.responseBody 
                  : JSON.stringify(selectedRequest.responseBody, null, 2)}
              </pre>
            </div>
          )}
          
          {selectedRequest.error && (
            <div className="text-red-400 text-xs">
              <span className="font-medium">Error:</span> {selectedRequest.error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

